import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteNav from "@/components/feature/SiteNav";
import SiteFooter from "@/components/feature/SiteFooter";
import { ArticleCard } from "@/components/feature/ArticleCard";
import { articles, categories, site } from "@/mocks/siteData";

export default function ArticlePage() {
  const { slug } = useParams();
  const [copied, setCopied] = useState(false);
  const article = articles.find((a) => a.slug === slug);
  const cat = categories.find((c) => c.slug === article?.categorySlug);

  const related = useMemo(() => {
    if (!article) return [];
    const same = articles.filter((a) => a.slug !== article.slug && a.categorySlug === article.categorySlug);
    const others = articles.filter((a) => a.slug !== article.slug && a.categorySlug !== article.categorySlug);
    return [...same, ...others].slice(0, 3);
  }, [article]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (article) {
      document.title = `${article.title} - 猎奇网`;
    }
  }, [article]);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* noop */
    }
  }

  if (!article || !cat) {
    return (
      <div className="bg-background-50">
        <SiteNav />
        <main className="mx-auto flex min-h-[70vh] max-w-[1400px] flex-col items-center justify-center px-4 text-center">
          <p className="font-heading text-3xl font-black text-primary-700">404</p>
          <h1 className="mt-3 font-heading text-xl font-bold text-foreground-900">这篇文章已沉入地下</h1>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.cover,
    datePublished: article.date,
    author: { "@type": "Person", name: article.author },
    publisher: { "@type": "Organization", name: site.name },
    inLanguage: "zh-CN",
    articleSection: cat.name,
    keywords: article.tags.join(","),
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "猎奇网", item: "https://0p622.cn/" },
        { "@type": "ListItem", position: 2, name: cat.name, item: `https://0p622.cn/explore/${cat.slug}` },
        { "@type": "ListItem", position: 3, name: article.title },
      ],
    },
  };

  return (
    <div className="bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      <SiteNav />

      {/* dark header */}
      <section className="relative overflow-hidden bg-foreground-950 text-background-50">
        <img
          src={article.cover}
          alt={`${article.title} · ${cat.name} 现场图`}
          className="absolute inset-0 h-full w-full object-cover object-top opacity-35 blur-[1px]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-foreground-950/55 to-foreground-950"></div>
        <div className="relative mx-auto max-w-4xl px-4 pb-12 pt-28 md:px-6 md:pt-40">
          <nav aria-label="面包屑" className="mb-6 text-xs text-background-100/70">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li>
                <Link to="/" className="transition-colors hover:text-primary-300">
                  首页
                </Link>
              </li>
              <li aria-hidden="true">
                <i className="ri-arrow-right-s-line"></i>
              </li>
              <li>
                <Link
                  to={`/explore/${cat.slug}`}
                  className="transition-colors hover:text-primary-300"
                >
                  {cat.name}
                </Link>
              </li>
              <li aria-hidden="true">
                <i className="ri-arrow-right-s-line"></i>
              </li>
              <li className="text-background-100/55">正文</li>
            </ol>
          </nav>
          <span className="inline-flex items-center rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
            {cat.name}
          </span>
          <h1 className="mt-4 text-balance font-heading text-3xl font-black leading-tight md:text-5xl">
            {article.title}
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-100/80 md:text-base">
            {article.excerpt}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-background-100/70">
            <span className="flex items-center gap-1.5">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-500/80">
                <i className="ri-user-3-line" aria-hidden="true"></i>
              </span>
              作者 · {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" aria-hidden="true"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line" aria-hidden="true"></i>
              阅读约 {article.readTime}
            </span>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-[1400px] px-4 py-12 md:px-6 md:py-16">
        <div className="grid gap-10 lg:grid-cols-[1fr_320px]">
          {/* body */}
          <article
            aria-label="正文"
            className="mx-auto w-full max-w-3xl border-background-200 rounded-xl border bg-background-50 p-6 md:p-10"
          >
            <div className="prose-custom">
              {article.paragraphs.map((p, i) => (
                <p
                  key={i}
                  className="mb-5 font-heading text-[17px] leading-[1.9] text-foreground-800 first:drop-cap"
                >
                  {p}
                </p>
              ))}
            </div>

            <div className="mt-8 rounded-lg border-l-4 border-primary-500 bg-secondary-100/70 p-5">
              <p className="text-sm leading-relaxed text-foreground-600">
                {cat.name}栏目仍在对相关史料进行核对与补录。欢迎就文中所涉观点留言讨论，或把线索投稿至{" "}
                <a href={`mailto:${site.contact.email}`} className="text-primary-700 underline decoration-primary-300 underline-offset-2">
                  {site.contact.email}
                </a>
                。
              </p>
            </div>

            {/* tags + actions */}
            <div className="mt-8 flex flex-col gap-4 border-t border-background-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
              <ul className="flex flex-wrap gap-2">
                {article.tags.map((t) => (
                  <li key={t}>
                    <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800">
                      <i className="ri-price-tag-3-line" aria-hidden="true"></i>
                      {t}
                    </span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={copyLink}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className={`${copied ? "ri-check-line text-primary-600" : "ri-link"} `} aria-hidden="true"></i>
                {copied ? "链接已复制" : "复制链接"}
              </button>
            </div>
          </article>

          {/* aside */}
          <aside className="space-y-6">
            <div className="rounded-xl border border-background-200 bg-background-100 p-6">
              <p className="text-xs font-semibold tracking-[0.18em] text-primary-700">ABOUT THE REPORTER</p>
              <div className="mt-4 flex items-center gap-3">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-500 text-lg font-black text-background-50">
                  {article.author.slice(0, 1)}
                </span>
                <div>
                  <p className="font-heading font-bold text-foreground-900">{article.author}</p>
                  <p className="text-xs text-foreground-500">猎奇网栏目作者</p>
                </div>
              </div>
              <p className="mt-4 text-xs leading-relaxed text-foreground-600">
                长期追踪{cat.name}方向，相信史料与物证比传说更可靠，也保留让谜团继续是谜团的诚实。
              </p>
            </div>

            <div className="rounded-xl border border-background-200 bg-background-100 p-6">
              <h2 className="text-sm font-semibold tracking-[0.18em] text-primary-700">栏目导航</h2>
              <ul className="mt-4 space-y-2">
                {categories.map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={`/explore/${c.slug}`}
                      className="flex items-center justify-between rounded-md px-2 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-500/10 hover:text-primary-700"
                    >
                      <span>{c.name}</span>
                      <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>

        {/* related */}
        <section aria-label="相关推荐" className="mt-16">
          <div className="mb-6 flex items-end justify-between">
            <h2 className="font-heading text-2xl font-bold text-foreground-900">沿着这条线索继续挖</h2>
            <Link
              to={`/explore/${cat.slug}`}
              className="inline-flex items-center gap-2 whitespace-nowrap text-sm font-medium text-primary-700 hover:text-accent-600"
            >
              更多{cat.name}
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}